import React from 'react';
import {
  ArrowRight,
  Boxes,
  Database,
  KeyRound,
  Layers,
  PlugZap,
  Search,
  ShieldCheck,
  Terminal,
  Zap,
} from 'lucide-react';
import TerminalCast from '../TerminalCast';
import { CodeBlock } from '../CodeBlock';
import { ADDONS } from './addonsData';

const featured = ADDONS.filter((a) => a.featured);
const rest = ADDONS.filter((a) => !a.featured);

const environments = [
  { name: 'Deploys', detail: 'containers, workers, and sites get addon vars at boot', href: '/deploys' },
  { name: 'Coding agents', detail: 'agent runs see the same env as the app they work on', href: '/agent' },
  { name: 'Schedulers & queues', detail: 'cron agents and queue workers inherit project addons', href: '/schedulers' },
  { name: 'Notebooks', detail: 'hosted notebooks can read addon connection vars', href: '/notebooks' },
];

export const AddonsHubPage: React.FC = () => (
  <main className="overflow-hidden bg-black text-white">
    <section className="relative border-b border-white/10 px-4 pb-14 pt-24 sm:px-8 lg:px-12">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.12),transparent_55%)]" />
      <div className="relative mx-auto grid max-w-[1400px] gap-10 lg:grid-cols-2 lg:items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/58">
            <PlugZap size={12} className="text-sky-400" />
            Hosted addons
          </div>
          <h1 className="mt-6 max-w-[16ch] font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
            Attach a database in one command
          </h1>
          <p className="mt-6 max-w-xl text-base leading-8 text-white/72">
            Managed services that attach to any repo, deploy, or agent: GPU vector search, PostgreSQL with
            pgvector and PostGIS, Mongo, cache, auth, and an AI monitor. Connection strings are injected as
            environment variables for your app and agents.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="/addons/gobed" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-sky-400 px-6 text-sm font-bold text-black transition hover:bg-sky-300">
              gobed GPU search <ArrowRight size={16} />
            </a>
            <a href="/addons/postgres" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-6 text-sm font-bold text-white transition hover:bg-white hover:text-black">
              Hosted Postgres
            </a>
          </div>
        </div>
        <div>
          <TerminalCast src="/casts/cli-addons.cast" title="app addons: attach Postgres + gobed" rows={19} />
          <p className="mt-2.5 flex flex-wrap items-center gap-x-2 text-xs text-white/45">
            <Terminal className="h-3.5 w-3.5" />
            Demo of the <code className="font-mono">app addons</code> flow: create, inspect env, query.
          </p>
        </div>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-[1400px]">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-400/90">first-party flagships</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Search and storage, built in-house</h2>
        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          {featured.map((a) => (
            <a key={a.slug} href={a.href} className="group rounded-2xl border border-white/10 bg-white/[0.04] p-7 transition hover:border-sky-400/40 hover:bg-white/[0.05]">
              <div className="flex items-center justify-between">
                {a.slug === 'gobed' ? <Search size={22} className="text-sky-400" /> : <Database size={22} className="text-sky-400" />}
                <span className="rounded-full border border-sky-400/25 bg-sky-400/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-sky-300">
                  {a.eyebrow}
                </span>
              </div>
              <h3 className="mt-5 font-display text-2xl font-bold group-hover:text-sky-300">{a.name}</h3>
              <p className="mt-2 text-[15px] leading-7 text-white/72">{a.tagline}</p>
              <p className="mt-3 text-sm leading-7 text-white/60">{a.description}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {a.envVars.slice(0, 3).map((v) => (
                  <code key={v.name} className="rounded-md border border-white/10 bg-black/40 px-2 py-1 font-mono text-[11px] text-white/60">{v.name}</code>
                ))}
              </div>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-sky-400">
                Explore {a.name} <ArrowRight size={14} className="transition group-hover:translate-x-0.5" />
              </span>
            </a>
          ))}
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {rest.map((a) => (
            <div key={a.slug} id={a.slug} className="scroll-mt-24 rounded-2xl border border-white/10 bg-white/[0.04] p-5">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/45">{a.eyebrow}</p>
              <h3 className="mt-2 font-display text-lg font-bold">{a.name}</h3>
              <p className="mt-2 text-sm leading-6 text-white/65">{a.tagline}</p>
              <div className="mt-3 space-y-1.5">
                {a.envVars.slice(0, 3).map((v) => (
                  <p key={v.name} className="truncate font-mono text-[11px] text-white/45">{v.name}</p>
                ))}
              </div>
              {a.plans ? (
                <p className="mt-3 text-xs text-white/50">from {a.plans[0].credits}</p>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-[1400px]">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-400/90">supported environments</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Where addon variables appear</h2>
        <p className="mt-5 max-w-3xl text-base leading-8 text-white/72">
          Addon variables merge in a fixed order — account, project, then addon — so an addon-provided{' '}
          <code className="font-mono text-sm">DATABASE_URL</code> lands everywhere the project runs. Agents
          see the same environment as your app, so an agent can run migrations, seed data, and verify
          queries without a manual credential handoff.
        </p>
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {environments.map((e) => (
            <a key={e.name} href={e.href} className="group rounded-xl border border-white/10 bg-white/[0.04] p-5 transition hover:border-white/25">
              <Zap size={16} className="text-sky-400" />
              <h3 className="mt-3 font-display text-base font-bold group-hover:text-sky-300">{e.name}</h3>
              <p className="mt-1 text-sm leading-6 text-white/60">{e.detail}</p>
            </a>
          ))}
        </div>
        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-white/45">
              <Terminal size={14} className="text-sky-400" /> CLI
            </p>
            <CodeBlock
              className="mt-4"
              language="bash"
              preClassName="!text-[13px] !leading-6"
              code={`app addons create --type postgres --plan starter --attach-default
app addons create --type gobed --plan gpu
app addons env <addon-id>   # print injected variables
app addons list`}
            />
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-white/45">
              <Boxes size={14} className="text-sky-400" /> appnz.yaml
            </p>
            <CodeBlock
              className="mt-4"
              language="yaml"
              preClassName="!text-[13px] !leading-6"
              code={`name: my-app
runtime: static
addons:
  - type: postgres
    plan: starter
  - type: gobed
    plan: shared`}
            />
          </div>
        </div>
        <div className="mt-6 flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <ShieldCheck size={18} className="mt-0.5 shrink-0 text-sky-400" />
          <p className="text-sm leading-7 text-white/65">
            Secret-marked variables (passwords, service keys) are injected at runtime, scrubbed from build
            and CI logs, and rotatable per addon — the same hygiene as{' '}
            <a href="/secrets" className="font-semibold text-sky-400 hover:text-sky-300">project secrets</a>.
            Billing meters into the shared <a href="/pricing" className="font-semibold text-sky-400 hover:text-sky-300">credit balance</a>.
          </p>
        </div>
      </div>
    </section>

    <section className="px-4 py-14 sm:px-8 sm:py-20 lg:px-12">
      <div className="relative mx-auto max-w-[1400px] overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center sm:px-12">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(56,189,248,0.14),transparent_60%)]" />
        <div className="relative">
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-4xl">Attach your first addon</h2>
          <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-8 text-white/70">
            Read the flagship pages, or install the CLI and attach an addon.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href="/addons/gobed" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-sky-400 px-6 text-sm font-bold text-black transition hover:bg-sky-300">
              <Search size={16} /> gobed
            </a>
            <a href="/addons/postgres" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-6 text-sm font-bold text-white transition hover:bg-white hover:text-black">
              <Database size={16} /> Postgres
            </a>
            <a href="/cli" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-6 text-sm font-bold text-white transition hover:bg-white hover:text-black">
              <Terminal size={16} /> Install the CLI
            </a>
          </div>
        </div>
      </div>
    </section>
  </main>
);

export default AddonsHubPage;
